import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AppThunk } from '../store'
import { setErrors } from '../common/errorsSlice'
import {
  DataExists,
  CardResponse,
  RankingResponse,
  DailyEngagementResponse,
  DailyLoadFactorGraphResponse,
  DailyPerformanceResponse,
  DailyConcentrationResponse,
  DashboardContentRequest,
  DashboardWeeklyContentRequest,
  WeeklyGraphResponse,
  DashboardRangeContentRequest,
} from '../../types/dashboard'
import {
  getDailyDataExists,
  getWeeklyDataExists,
  getDailyEngagementCard,
  getDailyPerformanceCard,
  getDailyConcentrationCard,
  getDailyLoadFactorCard,
  getDailyEngagementRanking,
  getDailyPerformanceRanking,
  getDailyConcentrationRanking,
  getDailyLoadFactorRanking,
  getDailyTotalRanking,
  getDailyEngagementGraph,
  getDailyLoadFactorGraph,
  getDailyPerformanceGraph,
  getDailyConcentrationGraph,
  getWeeklyPerformanceCard,
  getWeeklyEngagementCard,
  getWeeklyConcentrationCard,
  getWeeklyLoadFactorCard,
  getWeeklyEngagementRanking,
  getWeeklyPerformanceRanking,
  getWeeklyConcentrationRanking,
  getWeeklyLoadFactorRanking,
  getWeeklyTotalRanking,
  getWeeklyEngagementGraph,
  getWeeklyPerformanceGraph,
  getWeeklyConcentrationGraph,
  getWeeklyLoadFactorGraph,
  getRangeDataExists,
  getRangeConcentrationCard,
  getRangeConcentrationRanking,
  getRangeConcentrationGraph,
  getRangeEngagementCard,
  getRangeEngagementGraph,
  getRangeEngagementRanking,
  getRangeLoadFactorCard,
  getRangeLoadFactorGraph,
  getRangeLoadFactorRanking,
  getRangePerformanceCard,
  getRangePerformanceGraph,
  getRangePerformanceRanking,
  getRangeTotalRanking,
} from '../../apis/dashboard'

interface DashboardState {
  dataExists: {
    exists: boolean | null
    target_members: string[] | null
    exist_members: string[] | null
    not_analyze_members: string[] | null
  }
  card: {
    engagement: CardResponse | null
    performance: CardResponse | null
    concentration: CardResponse | null
    loadFactor: CardResponse | null
  }
  ranking: {
    engagement: RankingResponse | null
    performance: RankingResponse | null
    concentration: RankingResponse | null
    loadFactor: RankingResponse | null
    total: RankingResponse | null
  }
  graph: {
    engagement: DailyEngagementResponse | WeeklyGraphResponse | null
    performance: DailyPerformanceResponse | WeeklyGraphResponse | null
    concentration: DailyConcentrationResponse | WeeklyGraphResponse | null
    loadFactor: DailyLoadFactorGraphResponse | WeeklyGraphResponse | null
  }
  range_type: string | null
}

const initialState: DashboardState = {
  dataExists: {
    exists: null,
    target_members: null,
    exist_members: null,
    not_analyze_members: null,
  },
  card: {
    engagement: null,
    performance: null,
    concentration: null,
    loadFactor: null,
  },
  ranking: {
    engagement: null,
    performance: null,
    concentration: null,
    loadFactor: null,
    total: null,
  },
  graph: {
    engagement: null,
    performance: null,
    concentration: null,
    loadFactor: null,
  },
  range_type: null,
}

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    initDataExists: (state) => {
      state.dataExists.exists = null
      return state
    },
    buildDataExists: (state, action: PayloadAction<DataExists>) => {
      state.dataExists.exists = action.payload.exists
      state.dataExists.target_members = action.payload.target_members
      state.dataExists.exist_members = action.payload.exist_members
      state.dataExists.not_analyze_members = action.payload.not_analyze_members
      return state
    },
    setRangeType: (state, action) => {
      state.range_type = action.payload
    },
    // card
    getEngagementCardSuccess: (state, action: PayloadAction<CardResponse>) => {
      state.card.engagement = action.payload
      return state
    },
    getPerformanceCardSuccess: (state, action: PayloadAction<CardResponse>) => {
      state.card.performance = action.payload
      return state
    },
    getConcentrationCardSuccess: (state, action: PayloadAction<CardResponse>) => {
      state.card.concentration = action.payload
      return state
    },
    getLoadFactorCardSuccess: (state, action: PayloadAction<CardResponse>) => {
      state.card.loadFactor = action.payload
      return state
    },
    // ranking
    getEngagementRankingSuccess: (state, action: PayloadAction<RankingResponse>) => {
      state.ranking.engagement = action.payload
      return state
    },
    getPerformanceRankingSuccess: (state, action: PayloadAction<RankingResponse>) => {
      state.ranking.performance = action.payload
      return state
    },
    getConcentrationRankingSuccess: (state, action: PayloadAction<RankingResponse>) => {
      state.ranking.concentration = action.payload
      return state
    },
    getLoadFactorRankingSuccess: (state, action: PayloadAction<RankingResponse>) => {
      state.ranking.loadFactor = action.payload
      return state
    },
    getTotalRankingSuccess: (state, action: PayloadAction<RankingResponse>) => {
      state.ranking.total = action.payload
      return state
    },
    // graph
    getEngagementGraphSuccess: (state, action: PayloadAction<DailyEngagementResponse>) => {
      state.graph.engagement = action.payload
      return state
    },
    getPerformanceGraphSuccess: (state, action: PayloadAction<DailyPerformanceResponse>) => {
      state.graph.performance = action.payload
      return state
    },
    getConcentrationGraphSuccess: (state, action: PayloadAction<DailyConcentrationResponse>) => {
      state.graph.concentration = action.payload
      return state
    },
    getLoadFactorGraphSuccess: (state, action: PayloadAction<DailyLoadFactorGraphResponse>) => {
      state.graph.loadFactor = action.payload
      return state
    },
  },
})

export const {
  initDataExists,
  buildDataExists,
  setRangeType,
  getEngagementCardSuccess,
  getPerformanceCardSuccess,
  getConcentrationCardSuccess,
  getLoadFactorCardSuccess,
  getEngagementRankingSuccess,
  getPerformanceRankingSuccess,
  getConcentrationRankingSuccess,
  getLoadFactorRankingSuccess,
  getTotalRankingSuccess,
  getEngagementGraphSuccess,
  getLoadFactorGraphSuccess,
  getPerformanceGraphSuccess,
  getConcentrationGraphSuccess,
} = dashboardSlice.actions
export const dashboardReducer = dashboardSlice.reducer

export const resetDataExists = (
): AppThunk => async (dispatch) => {
  dispatch(initDataExists())
}

export const fetchDailyDataExists = (
  params: DashboardContentRequest,
): AppThunk => async (dispatch) => {
  dispatch(initDataExists())
  getDailyDataExists(params)
    .then((result) => {
      dispatch(buildDataExists(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchWeeklyDataExists = (
  params: DashboardWeeklyContentRequest,
): AppThunk => async (dispatch) => {
  dispatch(initDataExists())
  getWeeklyDataExists(params)
    .then((result) => {
      dispatch(buildDataExists(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchRangeDataExists = (
  params: DashboardRangeContentRequest,
): AppThunk => async (dispatch) => {
  dispatch(initDataExists())
  getRangeDataExists(params)
    .then((result) => {
      dispatch(buildDataExists(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchRangeType = (
  params: string | null,
): AppThunk => async (dispatch) => {
  dispatch(setRangeType(params))
}

export const fetchDailyEngagementCard = (
  params: DashboardContentRequest,
): AppThunk => async (dispatch) => {
  getDailyEngagementCard(params)
    .then((result) => {
      dispatch(getEngagementCardSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchWeeklyEngagementCard = (
  params: DashboardWeeklyContentRequest,
): AppThunk => async (dispatch) => {
  getWeeklyEngagementCard(params)
    .then((result) => {
      dispatch(getEngagementCardSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchRangeEngagementCard = (
  params: DashboardRangeContentRequest,
): AppThunk => async (dispatch) => {
  getRangeEngagementCard(params)
    .then((result) => {
      dispatch(getEngagementCardSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchDailyPerformanceCard = (
  params: DashboardContentRequest,
): AppThunk => async (dispatch) => {
  getDailyPerformanceCard(params)
    .then((result) => {
      dispatch(getPerformanceCardSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchWeeklyPerformanceCard = (
  params: DashboardWeeklyContentRequest,
): AppThunk => async (dispatch) => {
  getWeeklyPerformanceCard(params)
    .then((result) => {
      dispatch(getPerformanceCardSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchRangePerformanceCard = (
  params: DashboardRangeContentRequest,
): AppThunk => async (dispatch) => {
  getRangePerformanceCard(params)
    .then((result) => {
      dispatch(getPerformanceCardSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchDailyConcentrationCard = (
  params: DashboardContentRequest,
): AppThunk => async (dispatch) => {
  getDailyConcentrationCard(params)
    .then((result) => {
      dispatch(getConcentrationCardSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchWeeklyConcentrationCard = (
  params: DashboardWeeklyContentRequest,
): AppThunk => async (dispatch) => {
  getWeeklyConcentrationCard(params)
    .then((result) => {
      dispatch(getConcentrationCardSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchRangeConcentrationCard = (
  params: DashboardRangeContentRequest,
): AppThunk => async (dispatch) => {
  getRangeConcentrationCard(params)
    .then((result) => {
      dispatch(getConcentrationCardSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchDailyLoadCard = (
  params: DashboardContentRequest,
): AppThunk => async (dispatch) => {
  getDailyLoadFactorCard(params)
    .then((result) => {
      dispatch(getLoadFactorCardSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchWeeklyLoadCard = (
  params: DashboardWeeklyContentRequest,
): AppThunk => async (dispatch) => {
  getWeeklyLoadFactorCard(params)
    .then((result) => {
      dispatch(getLoadFactorCardSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchRangeLoadCard = (
  params: DashboardRangeContentRequest,
): AppThunk => async (dispatch) => {
  getRangeLoadFactorCard(params)
    .then((result) => {
      dispatch(getLoadFactorCardSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchDailyEngagementRanking = (
  params: DashboardContentRequest,
): AppThunk => async (dispatch) => {
  getDailyEngagementRanking(params)
    .then((result) => {
      dispatch(getEngagementRankingSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchWeeklyEngagementRanking = (
  params: DashboardWeeklyContentRequest,
): AppThunk => async (dispatch) => {
  getWeeklyEngagementRanking(params)
    .then((result) => {
      dispatch(getEngagementRankingSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchRangeEngagementRanking = (
  params: DashboardRangeContentRequest,
): AppThunk => async (dispatch) => {
  getRangeEngagementRanking(params)
    .then((result) => {
      dispatch(getEngagementRankingSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchDailyPerformanceRanking = (
  params: DashboardContentRequest,
): AppThunk => async (dispatch) => {
  getDailyPerformanceRanking(params)
    .then((result) => {
      dispatch(getPerformanceRankingSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchWeeklyPerformanceRanking = (
  params: DashboardWeeklyContentRequest,
): AppThunk => async (dispatch) => {
  getWeeklyPerformanceRanking(params)
    .then((result) => {
      dispatch(getPerformanceRankingSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchRangePerformanceRanking = (
  params: DashboardRangeContentRequest,
): AppThunk => async (dispatch) => {
  getRangePerformanceRanking(params)
    .then((result) => {
      dispatch(getPerformanceRankingSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchDailyConcentrationRanking = (
  params: DashboardContentRequest,
): AppThunk => async (dispatch) => {
  getDailyConcentrationRanking(params)
    .then((result) => {
      dispatch(getConcentrationRankingSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchWeeklyConcentrationRanking = (
  params: DashboardWeeklyContentRequest,
): AppThunk => async (dispatch) => {
  getWeeklyConcentrationRanking(params)
    .then((result) => {
      dispatch(getConcentrationRankingSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchRangeConcentrationRanking = (
  params: DashboardRangeContentRequest,
): AppThunk => async (dispatch) => {
  getRangeConcentrationRanking(params)
    .then((result) => {
      dispatch(getConcentrationRankingSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchDailyLoadRanking = (
  params: DashboardContentRequest,
): AppThunk => async (dispatch) => {
  getDailyLoadFactorRanking(params)
    .then((result) => {
      dispatch(getLoadFactorRankingSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchWeeklyLoadRanking = (
  params: DashboardWeeklyContentRequest,
): AppThunk => async (dispatch) => {
  getWeeklyLoadFactorRanking(params)
    .then((result) => {
      dispatch(getLoadFactorRankingSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchRangeLoadRanking = (
  params: DashboardRangeContentRequest,
): AppThunk => async (dispatch) => {
  getRangeLoadFactorRanking(params)
    .then((result) => {
      dispatch(getLoadFactorRankingSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchDailyTotalRanking = (
  params: DashboardContentRequest,
): AppThunk => async (dispatch) => {
  getDailyTotalRanking(params)
    .then((result) => {
      dispatch(getTotalRankingSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchWeeklyTotalRanking = (
  params: DashboardWeeklyContentRequest,
): AppThunk => async (dispatch) => {
  getWeeklyTotalRanking(params)
    .then((result) => {
      dispatch(getTotalRankingSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchRangeTotalRanking = (
  params: DashboardRangeContentRequest,
): AppThunk => async (dispatch) => {
  getRangeTotalRanking(params)
    .then((result) => {
      dispatch(getTotalRankingSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchDailyEngagementGraph = (
  params: DashboardContentRequest,
): AppThunk => async (dispatch) => {
  getDailyEngagementGraph(params)
    .then((result) => {
      dispatch(getEngagementGraphSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchWeeklyEngagementGraph = (
  params: DashboardWeeklyContentRequest,
): AppThunk => async (dispatch) => {
  getWeeklyEngagementGraph(params)
    .then((result) => {
      dispatch(getEngagementGraphSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchRangeEngagementGraph = (
  params: DashboardRangeContentRequest,
): AppThunk => async (dispatch) => {
  getRangeEngagementGraph(params)
    .then((result) => {
      dispatch(getEngagementGraphSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchDailyPerformanceGraph = (
  params: DashboardContentRequest,
): AppThunk => async (dispatch) => {
  getDailyPerformanceGraph(params)
    .then((result) => {
      dispatch(getPerformanceGraphSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchWeeklyPerformanceGraph = (
  params: DashboardWeeklyContentRequest,
): AppThunk => async (dispatch) => {
  getWeeklyPerformanceGraph(params)
    .then((result) => {
      dispatch(getPerformanceGraphSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchRangePerformanceGraph = (
  params: DashboardRangeContentRequest,
): AppThunk => async (dispatch) => {
  getRangePerformanceGraph(params)
    .then((result) => {
      dispatch(getPerformanceGraphSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchDailyConcentrationGraph = (
  params: DashboardContentRequest,
): AppThunk => async (dispatch) => {
  getDailyConcentrationGraph(params)
    .then((result) => {
      dispatch(getConcentrationGraphSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchWeeklyConcentrationGraph = (
  params: DashboardWeeklyContentRequest,
): AppThunk => async (dispatch) => {
  getWeeklyConcentrationGraph(params)
    .then((result) => {
      dispatch(getConcentrationGraphSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchRangeConcentrationGraph = (
  params: DashboardRangeContentRequest,
): AppThunk => async (dispatch) => {
  getRangeConcentrationGraph(params)
    .then((result) => {
      dispatch(getConcentrationGraphSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchDailyLoadFactorGraph = (
  params: DashboardContentRequest,
): AppThunk => async (dispatch) => {
  getDailyLoadFactorGraph(params)
    .then((result) => {
      dispatch(getLoadFactorGraphSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchWeeklyLoadFactorGraph = (
  params: DashboardWeeklyContentRequest,
): AppThunk => async (dispatch) => {
  getWeeklyLoadFactorGraph(params)
    .then((result) => {
      dispatch(getLoadFactorGraphSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchRangeLoadFactorGraph = (
  params: DashboardRangeContentRequest,
): AppThunk => async (dispatch) => {
  getRangeLoadFactorGraph(params)
    .then((result) => {
      dispatch(getLoadFactorGraphSuccess(result))
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}
